import React from 'react'

import {toString} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import {useNavigate} from 'react-router'
import * as Yup from 'yup'

import {useAppDispatch} from '../../hooks/redux'
import {fatalError} from '../../redux/features/core'
import {openCreateDynamicProductionTimeModal} from '../../redux/features/modal'

import useAsync from '../../../../commons/src/hooks/use-async'
import * as dynamicProductionTimeApi from '../../services/api/dynamic-production-time'
import * as providerInfoApi from '../../services/api/provider-info'
import {goToEditProviderInfo} from '../../services/navigation'
import {
  PossiblyMixedValue,
  isMixedValue,
  toMixedValidationSchema,
  toPossiblyMixedValue,
  toTouchedFormData,
} from '../utils/form'

import {
  DynamicProductionTime,
  DynamicProductionTimeId,
  EditProviderInfoTab,
  ProviderId,
  ProviderInfo,
} from '../../types'

import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Link from '../../../../commons/src/components/link'
import Paragraph from '../../../../commons/src/components/paragraph'
import SelectField from '../../../../commons/src/components/select-field'
import SelectMenu from '../../../../commons/src/components/select-menu'
import Stack from '../../../../commons/src/components/stack'

import SaveButtonPartial from '../save-button-partial'

export type DynamicProductionTimeOption = {
  label: string
  value: DynamicProductionTimeId
}

export type SingleFormData = {
  dynamicProductionTimeId: string
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

type MixedFormData = {
  dynamicProductionTimeId: PossiblyMixedValue<string>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export const initialValues = {
  dynamicProductionTimeId: '',
}

export const validationSchema = {
  dynamicProductionTimeId: Yup.string().label('Dynamic Production time').required(),
}

export const toFormData = (providerInfos: ProviderInfo[]): MixedFormData => ({
  dynamicProductionTimeId: toPossiblyMixedValue(
    providerInfos.map(providerInfo => providerInfo.dynamicProductionTimeId),
    toString
  ),
})

type Props = {
  providerId: ProviderId
  providerInfos: Array<ProviderInfo>
}

const EditDynamicProductionTimePartial = ({providerId, providerInfos}: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const {
    isLoading: isLoadingDynamicProductionTimes,
    data: dynamicProductionTimes = [],
    refresh,
  } = useAsync(() => dynamicProductionTimeApi.getByProvider(providerId), [], {
    onError: error => dispatch(fatalError({error})),
  })

  const formik = useFormik<MixedFormData>({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...toFormData(providerInfos),
    },
    validationSchema: toMixedValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const providerInfoRequest = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>

      try {
        const providerInfoIds = providerInfos.map(info => info.id)
        const nextProviderInfos = await providerInfoApi.patchMultiple(
          providerInfoRequest,
          providerInfoIds
        )

        goToEditProviderInfo(navigate, {
          providerInfos: nextProviderInfos,
          activeTab: EditProviderInfoTab.DYNAMIC_PRODUCTION_TIME,
          replace: true,
        })

        setIsSubmitted(true)
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  const setCreatedDynamicProductionTime = (dynamicProductionTime: DynamicProductionTime) => {
    refresh()
    formik.setFieldValue('dynamicProductionTimeId', dynamicProductionTime.id)
  }

  const dynamicProductionTimeOptions: DynamicProductionTimeOption[] = dynamicProductionTimes.map(
    ({id, name}) => ({
      label: name,
      value: id,
    })
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      <Paragraph>
        You can either select one of your existing dynamic production times or you can{' '}
        <Link
          onClick={() =>
            dispatch(
              openCreateDynamicProductionTimeModal({onDone: setCreatedDynamicProductionTime})
            )
          }
        >
          create a new one
        </Link>
        .
      </Paragraph>
      <Stack space="300">
        <FormRow>
          <FieldNote
            appearance="error"
            note={formik.submitCount > 0 && formik.errors.dynamicProductionTimeId}
          >
            <SelectField
              name="type"
              disabled={isLoadingDynamicProductionTimes}
              placeholder={
                isLoadingDynamicProductionTimes
                  ? 'Loading dynamic production times...'
                  : 'Select dynamic production time'
              }
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.dynamicProductionTimeId)}
              value={dynamicProductionTimeOptions.find(
                ({value}) => value === formik.values.dynamicProductionTimeId
              )}
              menu={<SelectMenu values={dynamicProductionTimeOptions} />}
              onChange={option => {
                formik.setFieldValue(
                  'dynamicProductionTimeId',
                  (option as DynamicProductionTimeOption).value
                )
              }}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
        </FormRow>
        <SaveButtonPartial formik={formik} isSubmitted={isSubmitted} />
      </Stack>
    </form>
  )
}

export default EditDynamicProductionTimePartial
